/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import { getActiveLocale } from '@/ux-common/src/utils/locale';

export const formatDateForDateRangeDisplay = (date: Date, locale: any) => {
  // return empty string if date is invalid
  if (isNaN(date.getTime())) return '';

  const activeLocale = getActiveLocale(locale);
  return new Intl.DateTimeFormat(activeLocale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

export const formatDateMonthTime = (dateString: string, locale?: string) => {
  if (dateString) {
    const date = new Date(dateString);
    // return empty string if date is invalid
    if (isNaN(date.getTime())) {
      return '';
    }
    const activeLocale = getActiveLocale(locale);
    return new Intl.DateTimeFormat(activeLocale, {
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  } else {
    return;
  }
};

export const formatDateForAppCards = (locale?: string) => {
  const date = Date.now();
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short',
  };

  const activeLocale = getActiveLocale(locale);
  const formatter = new Intl.DateTimeFormat(activeLocale, options);
  const formattedDate = formatter.format(date);
  return formattedDate;
};

export const formatToLocalISOString = (date: Date): string => {
  const offset = date.getTimezoneOffset();
  const adjustedDate = new Date(date.getTime() - offset * 60000);
  return adjustedDate.toISOString();
};

export const formatApiDate = (apiDate?: string, locale?: string): string => {
  if (!apiDate) return '';
  const date = new Date(apiDate);
  // return empty string if date is invalid
  if (isNaN(date.getTime())) return '';
  const activeLocale = getActiveLocale(locale);
  return new Intl.DateTimeFormat(activeLocale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

export function formatDateTime(
  inputDate: string | Date,
  locale?: string
): string {
  const date = new Date(inputDate);
  // return empty string if date is invalid
  if (isNaN(date.getTime())) return '';
  const activeLocale = getActiveLocale(locale);
  const formatted = new Intl.DateTimeFormat(activeLocale, {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  }).format(date);
  return `${formatted} (UTC)`;
}

/** Format an epoch-ms timestamp to "Jun 3, 11:05:44 am" */
export const formatTimestampToMonthDayTime = (
  ts: number,
  locale?: string
): string => {
  const date = new Date(ts);
  // return empty string if date is invalid
  if (isNaN(date.getTime())) return '';
  const activeLocale = getActiveLocale(locale);
  return new Intl.DateTimeFormat(activeLocale, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  }).format(date);
};

export function getCurrentTimestamp(locale?: string): string {
  const now = new Date();
  const activeLocale = getActiveLocale(locale);
  return new Intl.DateTimeFormat(activeLocale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(now);
}
